// ============================================================================
// "Reconcile with Zoho" — the manual safety net for webhooks that never arrived.
//
// The count below is the backlog, NOT the API cost: a paged `filter_by` listing
// classifies every open leg and only ones Zoho says moved are read, so hundreds
// of legs cost single-digit calls (see `oms/services/reconcile.py`). Still a
// button, because an operator who spotted a stuck leg beats a clock.
// ============================================================================
import { SyncOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Tooltip } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import {
  ReconcilePending,
  fetchReconcilePending,
  reconcileInbound,
} from "../oms.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";

const ReconcileButton: React.FC<{ onDone?: () => void }> = ({ onDone }) => {
  const { hasPermission } = usePermissions();
  const [pending, setPending] = useState<ReconcilePending | undefined>();
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    setPending(await fetchReconcilePending());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Manual Zoho reconcile — its own button-level permission.
  if (!hasPermission("oms.reconcile")) return null;

  const onRun = async () => {
    setRunning(true);
    try {
      await reconcileInbound();
      await refresh();
      onDone?.();
    } finally {
      setRunning(false);
    }
  };

  const total = pending?.total ?? 0;
  const label = total ? `Reconcile with Zoho (${total})` : "Reconcile with Zoho";

  return (
    <Popconfirm
      title="Reconcile with Zoho"
      description={
        total
          ? `Re-pull picklist and goods-receipt status for ${total} open leg${
              total > 1 ? "s" : ""
            } and replay anything missed. Zoho is listed in bulk first, so only the
             legs that have actually moved are read — a few API calls, not ${total}.`
          : "No open leg is currently waiting on Zoho — this will do nothing."
      }
      okText="Reconcile"
      onConfirm={onRun}
      disabled={running}
    >
      <Tooltip
        title={
          pending
            ? `${pending.picklists} pick leg(s), ${pending.receives} receive leg(s) open`
            : undefined
        }
      >
        <Button icon={<SyncOutlined />} loading={running} disabled={!total}>
          {label}
        </Button>
      </Tooltip>
    </Popconfirm>
  );
};

export default ReconcileButton;
